import {createAction} from '@reduxjs/toolkit';
import GeoJSON from 'ol/format/GeoJSON';
import KML from 'ol/format/KML';
import WKT from 'ol/format/WKT';
import GeometryCollection from 'ol/geom/GeometryCollection';
import {toast} from 'react-toastify';
import {read as readShapefile} from 'shapefile';
import {setExtent as setMapExtent} from '../actions/map.js';
import {
  ACCEPTED_FILE_TYPES,
  GEOJSON_FILE_TYPE,
  JSON_FILE_TYPE,
  KML_FILE_TYPE,
  SHAPEFILE_FILE_TYPE,
  WKT_FILE_TYPE,
} from '../common.js';

export const set = createAction('GEOMETRY_SET');
export const setByCoordinate = createAction('GEOMETRY_SET_BY_COORDINATE');
export const setByExtent = createAction('GEOMETRY_SET_BY_EXTENT');

const formatGeoJSON = new GeoJSON();
const formatKML = new KML();
const formatWKT = new WKT();

function getParserForExtension(extension) {
  if (extension === SHAPEFILE_FILE_TYPE) {
    return file => {
      return readShapefile(file).then(json => formatGeoJSON.readFeatures(json));
    };
  }

  let format;
  switch (extension) {
    case JSON_FILE_TYPE:
    case GEOJSON_FILE_TYPE:
      format = formatGeoJSON;
      break;
    case KML_FILE_TYPE:
      format = formatKML;
      break;
    case WKT_FILE_TYPE:
      format = formatWKT;
      break;
    default:
      // we don't cover one of the accepted types for some reason
      return null;
  }

  return file => {
    return new Promise(resolve => {
      let features;
      try {
        features = format.readFeatures(file);
      } catch (err) {
        throw new Error(`Invalid file: ${err.message}`);
      }

      resolve(features);
    });
  };
}

function createReaderLoadHandler(parser, dispatch) {
  return function (event) {
    parser(event.target.result)
      .then(features => {
        if (!features || !features.length) {
          throw new Error('No features found');
        }

        const geometries = features.map(feature => feature.getGeometry());
        const olGeometry =
          geometries.length === 1
            ? geometries[0]
            : new GeometryCollection(geometries);
        const geometry = formatGeoJSON.writeGeometryObject(olGeometry);
        dispatch(set(geometry));
        dispatch(setMapExtent(olGeometry.getExtent()));
      })
      .catch(err => {
        toast.error(err.message);
      });
  };
}

export const setByFile = file => dispatch => {
  const extension = '.' + file.name.split('.').pop().toLowerCase();
  if (!ACCEPTED_FILE_TYPES.includes(extension)) {
    const details = `Must be one of ${ACCEPTED_FILE_TYPES.join(', ')}`;
    toast.error(`Invalid file extension: ${details}`);
    return;
  }

  const parser = getParserForExtension(extension);
  if (!parser) {
    toast.error('File type not supported');
    return;
  }

  const reader = new FileReader();
  reader.onload = createReaderLoadHandler(parser, dispatch);
  if (extension === SHAPEFILE_FILE_TYPE) {
    reader.readAsArrayBuffer(file);
    return;
  }
  reader.readAsText(file);
};
